export const RETRY = 'RETRY';
export const NEXT_STEP_UNSPECIFIED = 'NEXT_STEP_UNSPECIFIED';
export const STATUS_REQUEST_FAILED = 'STATUS_REQUEST_FAILED';
export const START_VETTING = 'START_VETTING';
export const SHOW_RESULTS = 'SHOW_RESULTS';
export const SHOW_RESULTS_WITH_PARTNER = 'SHOW_RESULTS_WITH_PARTNER';

export const SHOW_RESULTS_WITH_AGENT = 'SHOW_RESULTS_WITH_AGENT';
export const SHOW_RESULTS_WITH_MORTGAGE = 'SHOW_RESULTS_WITH_MORTGAGE';
export const SHOW_RESULTS_WITH_AGENT_AND_MORTGAGE = 'SHOW_RESULTS_WITH_AGENT_AND_MORTGAGE';
export const EMPTY = 'EMPTY';

export const ALL_QUESTIONS_ANSWERED = 'ALL_QUESTIONS_ANSWERED';
export const SESSION_EXPIRED = 'SESSION_EXPIRED';
export const INELIGIBLE_EXIT = 'INELIGIBLE_EXIT';
export const SESSION_DISMISSED = 'SESSION_DISMISSED';
export const RETRY_TIMEOUT = 'RETRY_TIMEOUT';

export const COMPLETE_SESSION_RESPONSE_KEYS = [SHOW_RESULTS_WITH_PARTNER, SHOW_RESULTS_WITH_AGENT, SHOW_RESULTS_WITH_MORTGAGE, SHOW_RESULTS_WITH_AGENT_AND_MORTGAGE, EMPTY];
export const COMPLETE_SESSION_REASONS = [ALL_QUESTIONS_ANSWERED, SESSION_EXPIRED, INELIGIBLE_EXIT, SESSION_DISMISSED];

const MOCK_PHONE_TO_SESSION_START = {
    8000000001: 'veterans',
    8000000002: 'alliance',
    8000000003: 'realtor',
    8000000004: 'zumper',
    8000000005: 'apartment',
    8000000006: 'zoocasa',
};

const MOCK_PHONE_TO_SESSION_COMPLETE = {
    8000000011: 'veterans',
    8000000012: 'alliance',
    8000000013: 'realtor',
    8000000014: 'zumper',
    8000000015: 'apartment',
    8000000016: 'zoocasa',
    8000000017: 'agent',
    8000000018: 'mortgage',
    8000000019: 'agent_and_mortgage',
    8777804236: 'agent_and_mortgage',
};

const MOCK_PHONE_TO_SERVE_SCENARIO = {
    8000000101: 'show-results-partner-vu',
    8000000102: 'show-results-partner-arn',
    8000000103: 'retry-twice-then-partner-arn',
    8000000104: 'show-results-partner-realtor',
    8000000105: 'show-results-partner-zoocasa',
    8000000106: 'show-results-lo-only',
    8000000107: 'show-results-one-agent',
    8000000108: 'show-results-agent-and-lo',
    8000000111: 'start-vetting-lo-only',
    8000000112: 'start-vetting-one-agent',
    8000000113: 'start-vetting-agent-and-lo',
    8000000114: 'start-vetting-two-agents',
    8000000121: 'show-results-matching-in-progress',
    8000000122: 'show-results-no-referral-result',
    8000000123: 'show-results-no-referral-outcome',
    8000000124: 'start-vetting-matching-in-progress',
    8000000125: 'start-vetting-no-referral-result',
    8000000126: 'start-vetting-no-referral-outcome',
    8000000127: 'next-step-unspecified',
    8000000128: 'retry-multiple-times-then-timeout',
    8000000129: 'start-vetting-mortgage-preconfirmed-agent-and-lo',
    8000000130: 'retry-once-then-partner-arn',
    8000000131: 'start-vetting-dismissed',
    8000000199: 'unexpected-error',
};

export const normalizeMockPhone = (phone) => String(phone ?? '').replace(/\D/g, '');

export const sessionStartByPhone = (phone) => MOCK_PHONE_TO_SESSION_START[normalizeMockPhone(phone)] ?? null;

export const sessionCompleteByPhone = (phone) => MOCK_PHONE_TO_SESSION_COMPLETE[normalizeMockPhone(phone)] ?? null;

export const partnerTypeByPhone = sessionStartByPhone;

const MLS_PARTNER_KEY_BY_ID = {
    4001: 'zumper',
    4002: 'apartment',
};

export const partnerKeyByMlsId = (mlsId) => MLS_PARTNER_KEY_BY_ID[String(mlsId)] ?? null;

const SESSION_COMPLETE_INTENTS = new Set(['agent', 'mortgage', 'agent_and_mortgage']);

export const PARTNER_SESSION_COMPLETE_KEYS = new Set(['veterans', 'alliance']);

export const mockPartnerKeyByPhone = (phone) => {
    const sessionStart = sessionStartByPhone(phone);
    if (sessionStart) {
        return sessionStart;
    }
    const sessionComplete = sessionCompleteByPhone(phone);
    if (sessionComplete && !SESSION_COMPLETE_INTENTS.has(sessionComplete)) {
        return sessionComplete;
    }
    return null;
};

export const isPartnerSessionCompletePhone = (phone) => {
    const partnerKey = mockPartnerKeyByPhone(phone);
    return partnerKey != null && PARTNER_SESSION_COMPLETE_KEYS.has(partnerKey);
};

export const isPartnerFeedbackPhone = (phone) => {
    const partnerKey = partnerTypeByPhone(phone);
    return partnerKey != null && !PARTNER_SESSION_COMPLETE_KEYS.has(partnerKey);
};

export const intentByPhone = sessionCompleteByPhone;

export const isMockVettingPhone = (phone) => {
    const normalizedPhone = normalizeMockPhone(phone);
    return normalizedPhone.startsWith('80000000') || normalizedPhone === '8777804236';
};

const MOCK_PARTNER_PROFILES = {
    veterans: {
        partnerName: 'Veterans United Realty',
        partnerKey: 'veterans',
        partnerType: 'provider',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
    alliance: {
        partnerName: 'Alliance Realty Network',
        partnerKey: 'alliance',
        partnerType: 'provider',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
    rdc: {
        partnerName: 'Realtor New Homes',
        partnerKey: 'rdc',
        partnerType: 'provider',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
    zumper: {
        partnerName: 'Zumper',
        partnerKey: 'zumper',
        partnerType: 'provider',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
    apartment: {
        partnerName: 'Apartment List',
        partnerKey: 'apartment',
        partnerType: 'provider',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
    exp: {
        partnerName: 'Zoocasa by eXp Realty',
        partnerKey: 'exp',
        partnerType: 'provider',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
    realtor: {
        partnerName: 'Realtor.com',
        partnerKey: 'realtor',
        partnerType: 'provider',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
    proplus: {
        partnerName: 'Pro Plus',
        partnerKey: 'proplus',
        partnerType: 'provider',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
    zoocasa: {
        partnerName: 'Zoocasa',
        partnerKey: 'zoocasa',
        partnerType: 'provider',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
    movoto: {
        partnerName: 'Movoto',
        partnerKey: 'movoto',
        partnerType: 'provider',
        condition: 'fallback',
        nextStep: SHOW_RESULTS_WITH_PARTNER,
    },
};

export const getMockPartnerProfile = (partnerKey) => {
    const profile = MOCK_PARTNER_PROFILES[partnerKey];
    if (!profile) {
        return null;
    }
    return { ...profile };
};

export const getStaticPartnerId = (partner, environment = 'dev') => {
    const partners = {
        rdc: {
            dev: '00e75438-11d4-4868-a059-35b63b6ff9f1',
            stage: '0e831b78-2b51-44bb-b2f8-7bc9f06e6e7a',
            prod: '0e831b78-2b51-44bb-b2f8-7bc9f06e6e7a',
        },
        zumper: {
            dev: 'd75c012c-8fb8-49f9-9f7c-c9a49970239b',
            stage: '1f125803-b50c-4054-a486-e2ace27166e2',
            prod: '1f125803-b50c-4054-a486-e2ace27166e2',
        },
        apartment: {
            dev: 'fec4b624-de04-4acc-bd32-3c40d710db4b',
            stage: 'd9d87047-6ccf-470d-b882-adf5cdb68578',
            prod: 'd9d87047-6ccf-470d-b882-adf5cdb68578',
        },
        movoto: {
            dev: '56102ea5-ff59-4059-b3dd-2bdc30d7f189',
            stage: '572e9d28-239c-46ba-9626-1554b974cc98',
            prod: '572e9d28-239c-46ba-9626-1554b974cc98',
        },
        veterans: {
            dev: '740a166f-4b32-4995-963e-442df9d7690d',
            stage: '1333ce0e-6059-4816-b96a-259331520fbe',
            prod: '1333ce0e-6059-4816-b96a-259331520fbe',
        },
        alliance: {
            dev: 'c6733027-1064-40be-850a-f6a821c3ab8a',
            stage: 'c9be742f-324c-4874-bb3d-53da2c4b9231',
            prod: 'c9be742f-324c-4874-bb3d-53da2c4b9231',
        },
        realtor: {
            dev: 'ba032f48-4d11-47bc-ae3a-ceda2959422f',
            stage: '863082f9-3a73-4f19-9f7d-7dd8c665c5c2',
            prod: '863082f9-3a73-4f19-9f7d-7dd8c665c5c2',
        },
        exp: {
            dev: '30daa44c-df09-4a6e-b657-199997f15b0c',
            stage: '2524e801-e8fb-4b2d-84c6-4222b350fc16',
            prod: '2524e801-e8fb-4b2d-84c6-4222b350fc16',
        },
    };

    if (partners[partner] && partners[partner][environment]) {
        return partners[partner][environment];
    }
    return partner;
};

export const getMockPartnerProfilesForPhone = (phone, environment = 'dev') => {
    const mockKey = mockPartnerKeyByPhone(phone);
    if (!mockKey) {
        return null;
    }
    const primary = getMockPartnerProfile(mockKey);
    const partners = mockKey === 'movoto' ? [primary] : [primary, getMockPartnerProfile('movoto')];
    return partners.map((partner) => ({
        ...partner,
        partnerId: getStaticPartnerId(partner.partnerKey, environment),
    }));
};

export const pickRandomKey = (pool) => pool[Math.floor(Math.random() * pool.length)];

export const isValidCompleteKey = (key) => COMPLETE_SESSION_RESPONSE_KEYS.includes(key);

export const isValidCompleteReason = (reason) => COMPLETE_SESSION_REASONS.includes(reason);
